import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { BottomSheet } from '@/components/ui/dialog';
import { Skeleton, SkeletonLine } from '@/components/ui/skeleton';
import { api, type VpnApiDelivery } from '@/lib/api';
import { getWebApp, haptic } from '@/lib/telegram';
import { toast } from 'sonner';
import {
  Activity,
  Check,
  Copy,
  ExternalLink,
  KeyRound,
  Loader2,
  RefreshCw,
  Send,
  Webhook,
} from 'lucide-react';

/**
 * VPN Reseller API: ключ, вебхук, последние доставки и ссылка на доку. Зеркало
 * страницы `/api/vpn/app` на сайте (паритет держит check-parity.mjs, а файл
 * `routes/vpn/app/vpn-api.tsx` там остался редиректом ровно ради него).
 */
export const Route = createFileRoute('/vpn-api')({ component: VpnApiPage });

const DOCS_URL = 'https://hexpartners.pro/api/vpn/docs';
const DASHBOARD_URL = 'https://hexpartners.pro/api/vpn/app';

/** Подписи операций. Полный журнал и разбор ошибок живут на сайте. */
const OPERATION_RU: Record<string, string> = {
  'subscription.create': 'Выдача',
  'subscription.trial': 'Пробный период',
  'subscription.renew': 'Продление',
  'subscription.renew_custom': 'Продление на срок',
  'subscription.upgrade': 'Смена тарифа',
  'subscription.traffic': 'Трафик',
  'deposit.create': 'Пополнение',
};

function errText(e: unknown, fallback: string): string {
  // ApiError кладёт человеческий текст бэка в .message (см. humanizeApiError).
  return e instanceof Error && e.message ? e.message : fallback;
}

function openExternal(url: string) {
  const tg = (
    window as { Telegram?: { WebApp?: { openLink?: (u: string) => void } } }
  ).Telegram?.WebApp;
  if (tg?.openLink) tg.openLink(url);
  else window.open(url, '_blank');
}

function CopyRow({ value, masked = false }: { value: string; masked?: boolean }) {
  const [done, setDone] = useState(false);
  const shown = masked ? `${value.slice(0, 6)}${'•'.repeat(12)}` : value;
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          haptic('success');
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        });
      }}
      className="flex w-full items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5 text-left"
    >
      <code className="min-w-0 flex-1 truncate font-mono text-[12px]">{shown}</code>
      {done ? <Check className="size-4 text-foreground" /> : <Copy className="size-4 text-muted-foreground" />}
    </button>
  );
}

function KeySection() {
  const qc = useQueryClient();
  const { data, isPending, isError, refetch, isFetching } = useQuery({
    queryKey: ['vpn-api-key'],
    queryFn: () => api.vpnApiKey(),
  });
  const [newKey, setNewKey] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'reissue' | 'revoke' | null>(null);

  const reissue = useMutation({
    mutationFn: () => api.reissueVpnApiKey(),
    onSuccess: (r) => {
      haptic('success');
      setNewKey(r.api_key);
      setConfirm(null);
      void qc.invalidateQueries({ queryKey: ['vpn-api-key'] });
    },
    onError: (e) => {
      haptic('error');
      toast.error(errText(e, 'Не удалось выпустить ключ.'));
    },
  });
  const revoke = useMutation({
    mutationFn: () => api.revokeVpnApiKey(),
    onSuccess: () => {
      haptic('success');
      setNewKey(null);
      setConfirm(null);
      toast.success('Ключ отозван');
      void qc.invalidateQueries({ queryKey: ['vpn-api-key'] });
    },
    onError: (e) => {
      haptic('error');
      toast.error(errText(e, 'Не удалось отозвать ключ.'));
    },
  });

  return (
    <div className="rounded-2xl bg-card p-4">
      <div className="flex items-center gap-2 text-[15px] font-semibold">
        <KeyRound className="size-4 text-foreground" /> Ключ API
      </div>
      <p className="mt-1 text-[13px] text-muted-foreground">
        Заголовок Authorization: Bearer на каждом запросе. Ключ тратит депозит: не публикуйте его.
      </p>
      {newKey ? (
        <div className="mt-3 space-y-2">
          <div className="text-[12px] text-amber-500">Сохраните ключ сейчас, он показывается один раз.</div>
          <CopyRow value={newKey} />
        </div>
      ) : isPending ? (
        <Skeleton className="mt-3 h-11 w-full rounded-xl" />
      ) : isError && !data ? (
        // Упавший запрос не «ключа нет»: иначе кнопка «Выпустить ключ» одним тапом
        // перевыпускала живой ключ, и интеграция партнёра отваливалась.
        <div className="mt-3 text-[13px] text-muted-foreground">
          Не удалось загрузить ключ. Проверьте связь и повторите.
        </div>
      ) : data?.has_key ? (
        <div className="mt-3 text-[13px] text-muted-foreground">
          Активный ключ <code className="font-mono text-foreground">{data.key_prefix}…</code>
          {data.last_used_at ? '' : ' · запросов ещё не было'}
        </div>
      ) : (
        <div className="mt-3 text-[13px] text-muted-foreground">Ключа пока нет.</div>
      )}
      {data && !data.api_enabled ? (
        <div className="mt-2 text-[12px] text-amber-500">Доступ к API закрыт площадкой.</div>
      ) : null}
      <div className="mt-3 flex gap-2">
        {!data && !newKey && isPending ? null : isError && !data && !newKey ? (
          // Повтор переводит запрос в pending, и на это время выше снова скелетон.
          <button
            type="button"
            disabled={isFetching}
            onClick={() => void refetch()}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-white/[0.06] py-2.5 text-[13px] font-semibold active:scale-[0.98] disabled:opacity-50"
          >
            Повторить
          </button>
        ) : data?.has_key ? (
          <>
            <button
              type="button"
              onClick={() => setConfirm('reissue')}
              className="flex-1 rounded-xl bg-primary py-2.5 text-[13px] font-semibold text-primary-foreground active:scale-[0.98]"
            >
              Перевыпустить
            </button>
            <button
              type="button"
              onClick={() => setConfirm('revoke')}
              className="flex-1 rounded-xl bg-destructive/15 py-2.5 text-[13px] font-semibold text-destructive active:scale-[0.98]"
            >
              Отозвать
            </button>
          </>
        ) : (
          <button
            type="button"
            disabled={reissue.isPending || isPending}
            onClick={() => reissue.mutate()}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-2.5 text-[13px] font-semibold text-primary-foreground active:scale-[0.98] disabled:opacity-50"
          >
            {reissue.isPending ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />}
            Выпустить ключ
          </button>
        )}
      </div>
      <BottomSheet
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm === 'revoke' ? 'Отозвать ключ?' : 'Перевыпустить ключ?'}
      >
        <p className="text-[13px] text-muted-foreground">
          {confirm === 'revoke'
            ? 'Интеграция перестанет работать сразу. Новый ключ можно выпустить в любой момент.'
            : 'Старый ключ перестанет работать сразу. Обновите его в интеграции до следующего запроса.'}
        </p>
        <button
          type="button"
          disabled={reissue.isPending || revoke.isPending}
          onClick={() => (confirm === 'revoke' ? revoke.mutate() : reissue.mutate())}
          className="mt-4 w-full rounded-xl bg-destructive py-3 text-[14px] font-semibold text-destructive-foreground active:scale-[0.98] disabled:opacity-50"
        >
          {confirm === 'revoke' ? 'Да, отозвать' : 'Да, перевыпустить'}
        </button>
      </BottomSheet>
    </div>
  );
}

const STATUS_LABEL: Record<string, string> = {
  pending: 'в очереди',
  sending: 'отправляется',
  delivered: 'доставлено',
  failed: 'ошибка, будет повтор',
  dead: 'не доставлено',
};

function WebhookSection() {
  const qc = useQueryClient();
  const { data, isPending } = useQuery({ queryKey: ['vpn-api-webhook'], queryFn: () => api.vpnApiWebhook() });
  const deliveries = useQuery({
    queryKey: ['vpn-api-webhook-deliveries'],
    queryFn: () => api.vpnApiWebhookDeliveries(20),
  });
  const [url, setUrl] = useState<string | null>(null);
  const [events, setEvents] = useState<string[] | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const value = url ?? data?.url ?? '';
  const chosen = events ?? data?.events ?? [];
  const all = data?.all_events ?? [];
  const dirty = url !== null || events !== null;

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['vpn-api-webhook'] });
    void qc.invalidateQueries({ queryKey: ['vpn-api-webhook-deliveries'] });
  };
  const save = useMutation({
    mutationFn: () => api.saveVpnApiWebhook({ url: value.trim() || null, events: chosen.length ? chosen : null }),
    onSuccess: () => {
      haptic('success');
      setUrl(null);
      setEvents(null);
      toast.success('Сохранено');
      invalidate();
    },
    onError: (e) => {
      haptic('error');
      toast.error(errText(e, 'Не удалось сохранить.'));
    },
  });
  const test = useMutation({
    mutationFn: () => api.testVpnApiWebhook(),
    onSuccess: (r) => {
      if (r.delivered) {
        haptic('success');
        toast.success(`Доставлено, HTTP ${r.http_status}`);
      } else {
        haptic('error');
        toast.error(r.error ?? 'Сервер не ответил 2xx.');
      }
      invalidate();
    },
    onError: (e) => {
      haptic('error');
      toast.error(errText(e, 'Не удалось отправить.'));
    },
  });
  const showSecret = useMutation({
    mutationFn: () => api.vpnApiWebhookSecret(),
    onSuccess: (r) => setSecret(r.secret),
    onError: () => toast.error('Не удалось получить секрет.'),
  });
  const reissueSecret = useMutation({
    mutationFn: () => api.reissueVpnApiWebhookSecret(),
    onSuccess: (r) => {
      haptic('success');
      setSecret(r.secret);
      toast.success('Секрет перевыпущен');
    },
    onError: () => {
      haptic('error');
      toast.error('Не удалось перевыпустить.');
    },
  });

  const toggle = (name: string) => {
    const base = chosen.length ? chosen : all;
    const next = base.includes(name) ? base.filter((x) => x !== name) : [...base, name];
    setEvents(next.length === all.length ? [] : next);
  };

  return (
    <div className="rounded-2xl bg-card p-4">
      <div className="flex items-center gap-2 text-[15px] font-semibold">
        <Webhook className="size-4 text-foreground" /> Вебхук
      </div>
      <p className="mt-1 text-[13px] text-muted-foreground">
        События о подписках и депозите на ваш https-адрес. Подпись в заголовке X-Signature.
      </p>
      {isPending ? (
        <div className="mt-3 space-y-2">
          <Skeleton className="h-11 w-full rounded-xl" />
          <SkeletonLine className="w-2/3" />
        </div>
      ) : (
        <>
          <input
            value={value}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/vpn-webhook"
            spellCheck={false}
            className="mt-3 w-full rounded-xl border border-border bg-background px-3 py-2.5 font-mono text-[12px] outline-none"
          />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {all.map((name) => {
              const on = chosen.length === 0 || chosen.includes(name);
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => toggle(name)}
                  className={
                    on
                      ? 'rounded-full border border-white/25 bg-white/[0.07] px-2.5 py-1 font-mono text-[11px]'
                      : 'rounded-full border border-border px-2.5 py-1 font-mono text-[11px] text-muted-foreground'
                  }
                >
                  {name}
                </button>
              );
            })}
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            {chosen.length === 0 ? 'Все события, включая будущие.' : `Выбрано: ${chosen.length}.`}
          </div>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate()}
              className="flex-1 rounded-xl bg-primary py-2.5 text-[13px] font-semibold text-primary-foreground active:scale-[0.98] disabled:opacity-50"
            >
              Сохранить
            </button>
            <button
              type="button"
              disabled={test.isPending || !data?.is_active}
              onClick={() => test.mutate()}
              className="flex flex-1 items-center justify-center gap-1 rounded-xl bg-secondary py-2.5 text-[13px] font-semibold active:scale-[0.98] disabled:opacity-50"
            >
              <Send className="size-4" /> Тест
            </button>
          </div>
          <div className="mt-4 border-t border-border pt-3">
            <div className="text-[12px] text-muted-foreground">Секрет подписи</div>
            {secret ? (
              <div className="mt-2">
                <CopyRow value={secret} masked />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => showSecret.mutate()}
                className="mt-2 rounded-xl bg-secondary px-3 py-2 text-[13px] font-semibold"
              >
                Показать секрет
              </button>
            )}
            <button
              type="button"
              onClick={() => reissueSecret.mutate()}
              disabled={reissueSecret.isPending}
              className="mt-2 flex items-center gap-1 text-[12px] text-muted-foreground"
            >
              <RefreshCw className="size-3" /> Перевыпустить секрет
            </button>
          </div>
        </>
      )}
      <div className="mt-4 border-t border-border pt-3">
        <div className="text-[12px] text-muted-foreground">Последние доставки</div>
        {deliveries.isPending ? (
          <div className="mt-2 space-y-2">
            <SkeletonLine />
            <SkeletonLine className="w-4/5" />
          </div>
        ) : (deliveries.data?.items.length ?? 0) === 0 ? (
          <div className="mt-2 text-[13px] text-muted-foreground">Событий ещё не было.</div>
        ) : (
          <div className="mt-2 divide-y divide-border">
            {deliveries.data?.items.map((d: VpnApiDelivery) => (
              <div key={d.delivery_id} className="flex flex-wrap items-center gap-x-2 py-2 text-[12px]">
                <code className="font-mono">{d.event}</code>
                <span className="text-muted-foreground">{STATUS_LABEL[d.status] ?? d.status}</span>
                {d.http_status ? <span className="font-mono text-muted-foreground">HTTP {d.http_status}</span> : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Короткая сводка расхода. Полный дашборд с графиком и журналом за сотни строк
 * живёт на сайте: в мини-аппе такая таблица нечитаема, а вопрос «сколько ушло
 * денег и что упало» человек задаёт себе здесь же, рядом с ключом.
 */
function UsageSection() {
  const stats = useQuery({ queryKey: ['vpn-api-stats'], queryFn: () => api.vpnApiStats('month') });
  const recent = useQuery({
    queryKey: ['vpn-api-operations', 'recent'],
    queryFn: () => api.vpnApiOperations({ limit: 3 }),
  });

  return (
    <div className="rounded-2xl bg-card p-4">
      <div className="flex items-center gap-2 text-[15px] font-semibold">
        <Activity className="size-4 text-foreground" /> За 30 дней
      </div>
      {stats.isPending ? (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14 w-full rounded-xl" />
          ))}
        </div>
      ) : stats.data ? (
        <>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Tile label="Депозит" value={`$${stats.data.deposit.spendable_usd}`} />
            <Tile label="Списано" value={`$${stats.data.spent_usd}`} />
            <Tile label="Выдано подписок" value={String(stats.data.subscriptions.created)} />
            <Tile
              label="Запросов"
              value={String(stats.data.operations.total)}
              hint={stats.data.operations.failed ? `${stats.data.operations.failed} с ошибкой` : undefined}
            />
          </div>
          {stats.data.operations.by_error.length ? (
            <div className="mt-2 text-[12px] text-muted-foreground">
              Чаще всего отказывает{' '}
              <code className="font-mono text-destructive">
                {stats.data.operations.by_error[0].error_code}
              </code>
              , {stats.data.operations.by_error[0].count} раз. Разбор кода есть в документации.
            </div>
          ) : null}
        </>
      ) : (
        <div className="mt-3 text-[13px] text-muted-foreground">Статистика недоступна.</div>
      )}

      {recent.isPending ? (
        <div className="mt-3 space-y-2">
          {[0, 1, 2].map((i) => (
            <SkeletonLine key={i} />
          ))}
        </div>
      ) : recent.data?.items.length ? (
        <div className="mt-3 space-y-1.5">
          {recent.data.items.map((op) => (
            <div key={op.custom_id} className="flex items-center gap-2 text-[12px]">
              <span className="text-foreground">{OPERATION_RU[op.operation] ?? op.operation}</span>
              <code className="min-w-0 flex-1 truncate font-mono text-muted-foreground">{op.custom_id}</code>
              <span className={op.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'}>
                {op.charged_usd ? `$${op.charged_usd}` : op.status === 'failed' ? 'ошибка' : ''}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => openExternal(DASHBOARD_URL)}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-secondary py-2.5 text-[13px] font-semibold"
      >
        <ExternalLink className="size-4" /> Полный дашборд на сайте
      </button>
    </div>
  );
}

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl bg-secondary/60 px-3 py-2">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="text-[16px] font-semibold tabular-nums">{value}</div>
      {hint ? <div className="text-[11px] text-destructive">{hint}</div> : null}
    </div>
  );
}

function VpnApiPage() {
  const navigate = useNavigate();
  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    tg.MainButton.hide();
    const onBack = () => navigate({ to: '/profile' });
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  return (
    <div className="space-y-3 p-4">
      <div>
        <h1 className="text-[20px] font-bold">API для интеграций</h1>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Выдавайте подписки из своего сайта или бота по закупочной цене с депозита.
        </p>
      </div>
      <KeySection />
      <UsageSection />
      <WebhookSection />
      <button
        type="button"
        onClick={() => openExternal(DOCS_URL)}
        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-card py-3 text-[14px] font-semibold"
      >
        <ExternalLink className="size-4" /> Документация API
      </button>
    </div>
  );
}
